'use client';

import { useEffect, useState } from 'react';
import { Heart, Trash2 } from 'lucide-react';
import { useLocale } from 'next-intl';
import { Link } from '@/i18n/routing';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { productPath, productsPath } from '@/lib/localized-paths';
import { formatConsumerCategory } from '@/lib/consumer-products';
import { useFavoritesStore } from '@/store/favorites-store';

const copy = {
  nl: {
    title: 'Favorieten',
    subtitle: 'Bewaar producten die je later wilt vergelijken of bestellen.',
    empty: 'Je hebt nog geen favorieten opgeslagen.',
    shop: 'Bekijk producten',
    view: 'Bekijk product',
    remove: 'Verwijderen',
  },
  de: {
    title: 'Favoriten',
    subtitle: 'Speichere Produkte, die du später vergleichen oder bestellen möchtest.',
    empty: 'Du hast noch keine Favoriten gespeichert.',
    shop: 'Produkte ansehen',
    view: 'Produkt ansehen',
    remove: 'Entfernen',
  },
  fr: {
    title: 'Favoris',
    subtitle: 'Enregistrez les produits a comparer ou commander plus tard.',
    empty: 'Vous n avez pas encore de favoris.',
    shop: 'Voir les produits',
    view: 'Voir le produit',
    remove: 'Supprimer',
  },
};

export default function FavoritesPage() {
  const locale = useLocale();
  const t = copy[locale as keyof typeof copy] || copy.nl;
  const items = useFavoritesStore((state) => state.items);
  const removeFavorite = useFavoritesStore((state) => state.removeFavorite);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- standaard hydration-mount-detectie; er is geen ander moment om dit te weten dan in een effect
    setMounted(true);
  }, []);

  const visibleItems = mounted ? items : [];

  return (
    <div className="min-h-screen bg-[#fffaf4]">
      <section className="bg-[#fff3e4]">
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-rose-700">
              <Heart className="h-6 w-6 fill-current" />
            </div>
            <div>
              <h1 className="text-4xl font-black text-slate-950">{t.title}</h1>
              <p className="mt-2 text-slate-700">{t.subtitle}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-10">
        {visibleItems.length === 0 ? (
          <div className="rounded-2xl border border-amber-100 bg-white p-10 text-center shadow-sm">
            <p className="mb-5 text-slate-600">{t.empty}</p>
            <Button asChild>
              <Link href={productsPath(locale)}>{t.shop}</Link>
            </Button>
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {visibleItems.map((product) => (
              <Card key={product.id} className="overflow-hidden rounded-2xl border-slate-200 bg-white p-0 shadow-sm">
                <Link href={productPath(locale, product.slug)} className="block">
                  <div className="relative aspect-[4/3] bg-[#f7eadc]">
                    {product.image ? (
                      <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-5xl font-black text-rose-200">
                        {product.name.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="absolute left-4 top-4 rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-700 shadow-sm">
                      {formatConsumerCategory(product.category, locale)}
                    </div>
                  </div>
                </Link>
                <div className="p-5">
                  <h2 className="text-xl font-black text-slate-950">{product.name}</h2>
                  <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                    <Button asChild className="flex-1">
                      <Link href={productPath(locale, product.slug)}>{t.view}</Link>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => removeFavorite(product.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t.remove}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
